import { Typography, withStyles } from "@material-ui/core"
import classnames from "classnames"
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  memo,
} from "react"
import { Treebeard, decorators } from "react-treebeard"
import Loading from "../../../../../../../ReusableComponents/Loading"
import { useTracked } from "../../store"
import { resizeBounds } from "./SSEDevicePreview"

const TreeView_Styles = () => ({
  header: {
    padding: "3px 6px",
    color: "#66696b",
    display: "inline-block",
    borderRadius: "4px",
    margin: "2px 0px",
    transition: "all 0.1s ease-in-out",
    "&:hover": {
      backgroundColor: "#3f50b5",
      color: "white",
    },
  },
  header_active: {
    background: "rgba(17, 171, 58, 0.42)",
    "&:hover": {
      background: "rgba(17, 171, 58, 0.42)",
      color: "#66696b",
    },
  },
  faIcon: {
    marginRight: 6,
    fontSize: 16,
  },
})

// ── Immutable tree update helpers ───────────────────────────────────────────
// react-treebeard renders every node with a PureComponent (TreeNode) and a
// shallowequal Header, both keyed by `node.id`. To make a change visible we
// must return NEW node object references (so PureComponent re-renders), while
// keeping `id` stable (so React reconciles the DOM in place instead of
// remounting — which is what preserves the scroll position).
//
// Unchanged subtrees keep their original reference, so PureComponent skips
// them: only the affected node(s) and their ancestors actually re-render.
const applySelectById = (nodes, targetId) => {
  let changed = false
  const out = nodes.map((n) => {
    const nextActive = n.id === targetId
    let nextChildren = n.children
    if (n.children) {
      nextChildren = applySelectById(n.children, targetId)
    }
    if (n.active === nextActive && nextChildren === n.children) {
      return n
    }
    changed = true
    const next = { ...n, active: nextActive }
    if (n.children) next.children = nextChildren
    return next
  })
  return changed ? out : nodes
}

const applyToggleById = (nodes, targetId, toggled) => {
  let changed = false
  const out = nodes.map((n) => {
    let nextChildren = n.children
    if (n.children) {
      nextChildren = applyToggleById(n.children, targetId, toggled)
    }
    const nextToggled = n.id === targetId ? toggled : n.toggled
    if (n.toggled === nextToggled && nextChildren === n.children) {
      return n
    }
    changed = true
    const next = { ...n, toggled: nextToggled }
    if (n.children) next.children = nextChildren
    return next
  })
  return changed ? out : nodes
}

// Context that carries the select-only callback down to CustomContainer.
// Defined at module level so it's stable across renders.
const TreeViewSelectContext = createContext(null)

// TODO: nned to move useContext logic into a custom hook so we don't have to pass the select callback via context and can instead pass it directly to the Container via Treebeard's onToggle/onSelect APIs (which would also eliminate the need for immutable updates since the library would handle it internally).

// Overrides Treebeard's default Container which puts onClick on the WHOLE row
// (so any click toggles). We split it: arrow span → toggle only, label span →
// select only.
const CustomContainer = ({ style, decorators: d, terminal, onClick, node }) => {
  const onSelectNode = useContext(TreeViewSelectContext)
  return (
    <div
      style={{
        ...style.link,
        display: "flex",
        alignItems: "center",
        cursor: "default",
      }}
    >
      {/* Arrow: clicking here expands/collapses, does NOT select */}
      <span
        style={{ cursor: "pointer", minWidth: 16, flexShrink: 0, fontSize: 12 }}
        onClick={onClick}
      >
        {!terminal && (
          <i className={`fa fa-caret-${node.toggled ? "down" : "right"}`} />
        )}
      </span>
      {/* Label: clicking here selects, does NOT toggle */}
      <span
        onClick={(e) => {
          e.stopPropagation()
          onSelectNode?.(node)
        }}
      >
        <d.Header
          node={node}
          style={style.header}
        />
      </span>
    </div>
  )
}

// Header shows the node name + leaf icon only.
// Caret is rendered by CustomContainer so it's not duplicated here.
const Header_TreeView = ({ node, classes }) => {
  return (
    <div
      className={classnames([
        classes.header,
        node.active && classes.header_active,
      ])}
    >
      {!node.children && (
        <i className={classnames([classes.faIcon, "fa fa-dot-circle-o"])}></i>
      )}
      {node.name}
    </div>
  )
}

const WaitingForDevice = (props) => {
  const { withLoading, message } = props
  return (
    <div style={{ textAlign: "center" }}>
      {withLoading && <Loading size={24} />}
      <Typography
        variant={"subtitle2"}
        color={"primary"}
        style={{
          marginTop: 16,
          fontWeight: 600,
          backgroundColor: "#3f50b5cc",
          padding: "4px 12px",
          borderRadius: 4,
          color: "#ffffff",
        }}
      >
        {message}
      </Typography>
    </div>
  )
}

const Styled_Header_TreeView = withStyles(TreeView_Styles)(Header_TreeView)

// TODO: need to migrate to a more modern tree library (e.g. react-vtree) that supports this use case natively, so we don't have to do manual immutable updates and split toggle/select logic ourselves.
const TreeView = ({ handleViewSelect, ratio }) => {
  const [state] = useTracked()
  const { currentImage, treeView, selectedDevice } = state
  const waitingState = selectedDevice && selectedDevice !== ""

  // Local working copy of the tree so selection/toggle can be applied
  // immutably without mutating the store. Re-derived whenever the store's
  // treeView reference changes (i.e. a new SSE frame arrives).
  const [displayData, setDisplayData] = useState(treeView)
  const prevTreeViewRef = useRef(treeView)
  if (prevTreeViewRef.current !== treeView) {
    prevTreeViewRef.current = treeView
    setDisplayData(treeView)
  }

  // Stable refs so the (stable) handlers below never go stale.
  const handleViewSelectRef = useRef(handleViewSelect)
  handleViewSelectRef.current = handleViewSelect
  const ratioRef = useRef(ratio)
  ratioRef.current = ratio

  // Select-only: highlight the node + notify parent. Stable identity.
  const handleSelect = useCallback((node) => {
    const { sdk_id, sdk_path_id, view_bounds } = node
    handleViewSelectRef.current({
      id: sdk_id,
      path: sdk_path_id,
      tag: node?.sdk_tag !== "" ? node?.sdk_tag : node.name.props.tag,
      is_in_wv: node?.is_in_wv,
      wv_tag: node?.wv_tag,
      view_additional_info: node?.view_additional_info,
      view_bounds: resizeBounds(view_bounds, ratioRef.current)?.bounds,
      bounds: resizeBounds(view_bounds, ratioRef.current)?.bounds,
      views: node?.views ?? [],
      _HTML_ElementId: node.html_id,
    })
    setDisplayData((prev) => (prev ? applySelectById(prev, node.id) : prev))
  }, [])

  // Toggle-only: expand/collapse. Stable identity.
  const onToggle = useCallback((node, toggled) => {
    setDisplayData((prev) =>
      prev ? applyToggleById(prev, node.id, toggled) : prev,
    )
  }, [])

  // Memoized so TreeNode (PureComponent) doesn't re-render the whole tree when
  // TreeView re-renders for unrelated reasons (e.g. parent passes a new
  // handleViewSelect reference). Combined with stable node refs, this keeps
  // re-renders confined to the node that actually changed.
  const treebeardDecorators = useMemo(
    () => ({
      ...decorators,
      Header: Styled_Header_TreeView,
      Container: CustomContainer,
    }),
    [],
  )
  const treebeardStyle = useMemo(
    () => ({
      tree: {
        base: { backgroundColor: "transparent" },
        node: {
          activeLink: {
            backgroundColor: "transparent",
            background: "transparent",
          },
        },
      },
    }),
    [],
  )

  return (
    <TreeViewSelectContext.Provider value={handleSelect}>
      {currentImage && displayData && (
        <div
          style={{
            maxHeight: 516,
            overflow: "auto",
          }}
        >
          <Treebeard
            data={displayData}
            onToggle={onToggle}
            decorators={treebeardDecorators}
            style={treebeardStyle}
          />
        </div>
      )}
      {!currentImage && (
        <div style={{ maxWidth: "50%", margin: "auto" }}>
          <WaitingForDevice
            withLoading={waitingState}
            message={
              !waitingState
                ? "Please select a Test Device"
                : "Waiting for Device"
            }
          />
        </div>
      )}
    </TreeViewSelectContext.Provider>
  )
}

export default memo(TreeView)
